import * as tf from '@tensorflow/tfjs';
import { Landmark, CustomGesture } from '../types';

export class GestureClassifier {
  private model: tf.LayersModel | null = null;
  private labels: string[] = [
    'wave', 'thumbs_up', 'peace', 'fist', 'open_palm',
    'point', 'ok', 'stop', 'call_me', 'rock',
    'pinch', 'swipe_left', 'swipe_right', 'swipe_up', 'swipe_down',
    'zoom_in', 'zoom_out', 'rotate_cw', 'rotate_ccw', 'grab'
  ];
  
  private customGestures: CustomGesture[] = [];
  private gestureHistory: Array<{ name: string; confidence: number; timestamp: number }> = [];
  private readonly SMOOTHING_WINDOW = 5;
  private readonly CONFIDENCE_THRESHOLD = 0.7;

  async loadModel(): Promise<void> {
    console.log('Loading enhanced gesture classifier...');
    console.log('Using rule-based + temporal gesture detection');
  }

  setCustomGestures(gestures: CustomGesture[]): void {
    this.customGestures = gestures;
    console.log(`Loaded ${gestures.length} custom gestures`);
  }

  async predict(landmarks: Landmark[]): Promise<{
    gesture: string;
    confidence: number;
  }> {
    const result = this.detectGestureFromLandmarks(landmarks);
    
    // Check custom gestures first
    const customMatch = this.matchCustomGesture(landmarks);
    if (customMatch && customMatch.confidence > result.confidence) {
      return this.smoothGesture({ gesture: customMatch.name, confidence: customMatch.confidence });
    }
    
    return this.smoothGesture({ gesture: result.name, confidence: result.confidence });
  }

  private smoothGesture(gesture: { gesture: string; confidence: number }): {
    gesture: string;
    confidence: number;
  } {
    // Add to history
    this.gestureHistory.push({
      name: gesture.gesture,
      confidence: gesture.confidence,
      timestamp: Date.now()
    });

    // Keep only recent history
    const cutoff = Date.now() - 1000;
    this.gestureHistory = this.gestureHistory.filter(g => g.timestamp > cutoff);

    // If history is too short, return current
    if (this.gestureHistory.length < 3) {
      return gesture;
    }

    // Count occurrences in recent history
    const counts = new Map<string, number>();
    const confidences = new Map<string, number[]>();
    
    this.gestureHistory.slice(-this.SMOOTHING_WINDOW).forEach(g => {
      counts.set(g.name, (counts.get(g.name) || 0) + 1);
      if (!confidences.has(g.name)) confidences.set(g.name, []);
      confidences.get(g.name)!.push(g.confidence);
    });

    // Find most common gesture
    let maxCount = 0;
    let smoothedGesture = gesture.gesture;
    
    counts.forEach((count, name) => {
      if (count > maxCount) {
        maxCount = count;
        smoothedGesture = name;
      }
    });

    // Average confidence
    const avgConfidence = confidences.get(smoothedGesture)!.reduce((a, b) => a + b, 0) / 
                         confidences.get(smoothedGesture)!.length;

    return {
      gesture: smoothedGesture,
      confidence: avgConfidence
    };
  }

  private matchCustomGesture(landmarks: Landmark[]): { name: string; confidence: number } | null {
    if (this.customGestures.length === 0) return null;

    let bestMatch: { name: string; confidence: number } | null = null;
    let bestDistance = Infinity;

    for (const custom of this.customGestures) {
      // Compare with each training sample
      for (const trainingLandmarks of custom.trainingData) {
        const distance = this.calculateLandmarkDistance(landmarks, trainingLandmarks);
        
        if (distance < bestDistance) {
          bestDistance = distance;
          // Convert distance to confidence (lower distance = higher confidence)
          const confidence = Math.max(0, 1 - distance / 0.5);
          bestMatch = {
            name: custom.name,
            confidence
          };
        }
      }
    }

    return bestMatch && bestMatch.confidence > this.CONFIDENCE_THRESHOLD ? bestMatch : null;
  }

  private calculateLandmarkDistance(landmarks1: Landmark[], landmarks2: Landmark[]): number {
    if (landmarks1.length !== landmarks2.length) return Infinity;

    let totalDistance = 0;
    for (let i = 0; i < landmarks1.length; i++) {
      const dx = landmarks1[i].x - landmarks2[i].x;
      const dy = landmarks1[i].y - landmarks2[i].y;
      const dz = landmarks1[i].z - landmarks2[i].z;
      totalDistance += Math.sqrt(dx * dx + dy * dy + dz * dz);
    }

    return totalDistance / landmarks1.length;
  }

  private detectGestureFromLandmarks(landmarks: Landmark[]): {
    name: string;
    confidence: number;
  } {
    const fingerStates = this.getFingerStates(landmarks);
    const extendedCount = fingerStates.filter(s => s).length;
    
    // Calculate hand orientation and movement
    const palmNormal = this.calculatePalmNormal(landmarks);
    const fingerDirection = this.calculateFingerDirection(landmarks);
    
    // Open palm - all fingers extended
    if (extendedCount === 5) {
      if (Math.abs(palmNormal.z) > 0.7) {
        return { name: 'stop', confidence: 0.95 };
      }
      return { name: 'open_palm', confidence: 0.95 };
    }
    
    // Fist - no fingers extended
    if (extendedCount === 0) {
      return { name: 'fist', confidence: 0.95 };
    }
    
    // Peace sign - index and middle finger extended
    if (fingerStates[1] && fingerStates[2] && !fingerStates[3] && !fingerStates[4]) {
      return { name: 'peace', confidence: 0.92 };
    }
    
    // Thumbs up - only thumb extended
    if (fingerStates[0] && !fingerStates[1] && !fingerStates[2] && !fingerStates[3] && !fingerStates[4]) {
      return { name: 'thumbs_up', confidence: 0.92 };
    }
    
    // Point - only index finger extended
    if (!fingerStates[0] && fingerStates[1] && !fingerStates[2] && !fingerStates[3] && !fingerStates[4]) {
      return { name: 'point', confidence: 0.90 };
    }
    
    // OK sign - thumb and index forming circle
    if (this.isOKSign(landmarks)) {
      return { name: 'ok', confidence: 0.88 };
    }
    
    // Call me - thumb and pinky extended
    if (fingerStates[0] && !fingerStates[1] && !fingerStates[2] && !fingerStates[3] && fingerStates[4]) {
      return { name: 'call_me', confidence: 0.88 };
    }
    
    // Rock sign - index and pinky extended
    if (!fingerStates[0] && fingerStates[1] && !fingerStates[2] && !fingerStates[3] && fingerStates[4]) {
      return { name: 'rock', confidence: 0.88 };
    }
    
    // Pinch - thumb and index close together
    if (this.isPinch(landmarks)) {
      return { name: 'pinch', confidence: 0.85 };
    }
    
    // Wave - open palm with movement
    if (extendedCount >= 4) {
      return { name: 'wave', confidence: 0.85 };
    }
    
    // Grab - partially closed hand
    if (extendedCount >= 1 && extendedCount <= 2) {
      return { name: 'grab', confidence: 0.80 };
    }
    
    return { name: 'unknown', confidence: 0.5 };
  }

  private getFingerStates(landmarks: Landmark[]): boolean[] {
    const fingerTips = [4, 8, 12, 16, 20];
    const fingerPips = [3, 6, 10, 14, 18];
    
    const states: boolean[] = [];
    
    for (let i = 0; i < fingerTips.length; i++) {
      const tip = landmarks[fingerTips[i]];
      const pip = landmarks[fingerPips[i]];
      
      if (i === 0) {
        states.push(Math.abs(tip.x - pip.x) > 0.05);
      } else {
        states.push(tip.y < pip.y - 0.03);
      }
    }
    
    return states;
  }

  private calculatePalmNormal(landmarks: Landmark[]): { x: number; y: number; z: number } {
    // Use wrist, index base, and pinky base to calculate palm normal
    const wrist = landmarks[0];
    const indexBase = landmarks[5];
    const pinkyBase = landmarks[17];
    
    // Calculate two vectors on the palm
    const v1 = {
      x: indexBase.x - wrist.x,
      y: indexBase.y - wrist.y,
      z: indexBase.z - wrist.z
    };
    
    const v2 = {
      x: pinkyBase.x - wrist.x,
      y: pinkyBase.y - wrist.y,
      z: pinkyBase.z - wrist.z
    };
    
    // Cross product for normal
    const normal = {
      x: v1.y * v2.z - v1.z * v2.y,
      y: v1.z * v2.x - v1.x * v2.z,
      z: v1.x * v2.y - v1.y * v2.x
    };
    
    // Normalize
    const length = Math.sqrt(normal.x ** 2 + normal.y ** 2 + normal.z ** 2);
    return {
      x: normal.x / length,
      y: normal.y / length,
      z: normal.z / length
    };
  }

  private calculateFingerDirection(landmarks: Landmark[]): { x: number; y: number; z: number } {
    const wrist = landmarks[0];
    const middleTip = landmarks[12];
    
    const dx = middleTip.x - wrist.x;
    const dy = middleTip.y - wrist.y;
    const dz = middleTip.z - wrist.z;
    
    const length = Math.sqrt(dx ** 2 + dy ** 2 + dz ** 2);
    
    return {
      x: dx / length,
      y: dy / length,
      z: dz / length
    };
  }

  private isOKSign(landmarks: Landmark[]): boolean {
    const thumbTip = landmarks[4];
    const indexTip = landmarks[8];
    
    const distance = Math.sqrt(
      (thumbTip.x - indexTip.x) ** 2 +
      (thumbTip.y - indexTip.y) ** 2 +
      (thumbTip.z - indexTip.z) ** 2
    );
    
    return distance < 0.05;
  }

  private isPinch(landmarks: Landmark[]): boolean {
    const thumbTip = landmarks[4];
    const indexTip = landmarks[8];
    
    const distance = Math.sqrt(
      (thumbTip.x - indexTip.x) ** 2 +
      (thumbTip.y - indexTip.y) ** 2 +
      (thumbTip.z - indexTip.z) ** 2
    );
    
    return distance < 0.03;
  }

  getLabels(): string[] {
    return [...this.labels, ...this.customGestures.map(g => g.name)];
  }
}
